import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row, Table, Badge } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  fetchAppSettings,
  updateAppSettings,
  updateGameplayParams,
  fetchGameplayParams,
  getSettingsRO, updateSettingsRO, getSettingsSO, updateSettingsSO,
  getOperatorDropdown,
  getOperators,
  fetchOperatorGameplayParams,
  updateOperatorGameplayParams,
  getOperatorGames,
  getOperatorUsers
} from "../../helpers/apis";

import { useDispatch } from "react-redux";
import { showToast } from "../../slices/toast/reducer";
import { Settings as SettingsIcon, X as XIcon, User as UserIcon, CheckCircle } from "react-feather";
import Select from "react-select";
import { GAME_MODES, GAME_TYPES } from "../../common/constants";
import "./settings.css";

const Settings = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [commonSettings, setCommonSettings] = useState([]);
  const [ltSettings, setLtSettings] = useState([]);
  const [lt1Settings, setLt1Settings] = useState([]);
  const [roSettings, setRoSettings] = useState([]);
  const [soSettings, setSoSettings] = useState([]);
  const [form, setForm] = useState({});
  const [operators, setOperators] = useState([]);
  const [operatorGameplayParams, setOperatorGameplayParams] = useState([]);
  const [loadingOperatorGameplay, setLoadingOperatorGameplay] = useState(false);
  const [savingOperatorGameplay, setSavingOperatorGameplay] = useState(false);
  const [operatorUsers, setOperatorUsers] = useState([]);
  const [loadingOperatorUsers, setLoadingOperatorUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [operatorGameplayForm, setOperatorGameplayForm] = useState({
    operatorId: "",
    userId: "",
    lt: "",
    lt1: "",
    ro: "",
    so: ""
  });
  const [operatorStats, setOperatorStats] = useState(null);
  const [loadingOperatorStats, setLoadingOperatorStats] = useState(false);

  const loadSettings = React.useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch standard settings
      const response = await fetchAppSettings();
      const isSuccess = response && (response.status === "success" || response.status > 0 || response.statusCode === 200);

      let allData = [];
      if (isSuccess && response.data && Array.isArray(response.data)) {
        allData = response.data;
      } else if (isSuccess && Array.isArray(response)) {
        allData = response;
      }

      // 2. Fetch specialized gameplay params (to ensure they exist for both games)
      let gameplayParams = [];
      try {
        const gpResponse = await fetchGameplayParams(); // GET /api/app-settings/gameplay-params
        if (gpResponse && (gpResponse.status > 0 || gpResponse.status === "success" || gpResponse.statusCode === 200) && Array.isArray(gpResponse.data)) {
          gameplayParams = gpResponse.data;
        }
      } catch (err) {
        console.error("Failed to fetch specialized gameplay params", err);
      }

      // 3. Fetch specialized Roulette settings
      let roSpecialSettings = null;
      let soSpecialSettings = null;
      try {
        const soRes = await getSettingsSO();
        if (soRes && soRes.success && soRes.data) {
          soSpecialSettings = soRes.data;
        }
      } catch (err) {
        console.error("Failed to fetch SO specialized settings", err);
      }
      try {
        const roRes = await getSettingsRO();
        if (roRes && roRes.success && roRes.data) {
          roSpecialSettings = roRes.data;
        }
      } catch (err) {
        console.error("Failed to fetch RO specialized settings", err);
      }

      const common = [];

      const lt = [];
      const lt1 = [];
      const ro = [];
      const so = [];
      const initialForm = {};


      // Combine and categorize, ensuring uniqueness by name and type (case-insensitive)
      const uniqueMap = new Map();

      // 1. Process allData (general settings)
      allData.forEach(item => {
        if (!item.name) return;
        const key = `${item.name.toLowerCase()}_${item.type || GAME_TYPES.ALL}`;
        uniqueMap.set(key, item);
      });

      // 2. Process gameplayParams (specialized settings) - these override allData
      gameplayParams.forEach(gp => {
        if (!gp.name) return;
        const key = `${gp.name.toLowerCase()}_${gp.type || GAME_TYPES.ALL}`;
        uniqueMap.set(key, gp);
      });

      // Special case: Ensure LT1 gameplay_params is shown if missing
      const lt1Key = "gameplay_params_lt1";
      if (!uniqueMap.has(lt1Key)) {
        uniqueMap.set(lt1Key, { name: "gameplay_params", type: GAME_TYPES.LT1, value: GAME_MODES.MEDIUM });
      }

      // Special case: Ensure SO gameplay_params is shown if missing
      const soKey = "gameplay_params_so";
      if (!uniqueMap.has(soKey)) {
        uniqueMap.set(soKey, { name: "gameplay_params", type: GAME_TYPES.SO, value: GAME_MODES.HARD });
      }

      // Special case: Ensure RO gameplay_params is shown if missing
      const roKey = "gameplay_params_ro";
      if (!uniqueMap.has(roKey)) {
        uniqueMap.set(roKey, { name: "gameplay_params", type: GAME_TYPES.RO, value: GAME_MODES.HARD });
      }


      // Process merged data for LT and LT1
      const mergedData = Array.from(uniqueMap.values());

      mergedData.forEach((setting) => {
        if (setting.type === GAME_TYPES.LT) {
          lt.push(setting);
          initialForm[`lt_${setting.name}`] = setting.value ?? "";
        } else if (setting.type === GAME_TYPES.LT1) {
          lt1.push(setting);
          initialForm[`lt1_${setting.name}`] = setting.value ?? "";
        } else if (setting.type === GAME_TYPES.ALL) {
          common.push(setting);
          initialForm[setting.name] = setting.value ?? "";
        }
      });

      // Ensure unique common settings and avoid duplicate default coin fields
      const normalizeSettingName = (name) => String(name || "").replace(/[\s_]/g, "").toLowerCase();
      const commonMap = new Map();
      common.forEach((setting) => {
        const normalizedName = normalizeSettingName(setting?.name);
        if (!commonMap.has(normalizedName)) {
          commonMap.set(normalizedName, setting);
        }
      });
      const uniqueCommon = Array.from(commonMap.values());

      // Ensure Default Coin card is always visible in Common Settings
      const hasDefaultCoin = uniqueCommon.some((setting) => normalizeSettingName(setting.name) === "welcomebalance");
      if (!hasDefaultCoin) {
        uniqueCommon.unshift({
          name: "welcomeBalance",
          value: 100,
          type: GAME_TYPES.ALL
        });
        initialForm.welcomeBalance = initialForm.welcomeBalance ?? 100;
      }

      // Special handling for RO - Only show Game Mode as requested
      if (roSpecialSettings) {
        ro.push({
          _id: roSpecialSettings._id,
          name: "gameMode",
          value: roSpecialSettings.gameMode,
          type: GAME_TYPES.RO
        });
        initialForm[`ro_gameMode`] = roSpecialSettings.gameMode ?? GAME_MODES.MEDIUM;
      } else {
        ro.push({
          name: "gameMode",
          value: GAME_MODES.MEDIUM,
          type: GAME_TYPES.RO
        });
        initialForm[`ro_gameMode`] = GAME_MODES.MEDIUM;
      }


      // Special handling for SO
      if (soSpecialSettings) {
        so.push({
          _id: soSpecialSettings._id,
          name: "gameMode",
          value: soSpecialSettings.gameMode,
          type: GAME_TYPES.SO
        });
        initialForm[`so_gameMode`] = soSpecialSettings.gameMode ?? GAME_MODES.MEDIUM;
      } else {
        so.push({
          name: "gameMode",
          value: GAME_MODES.MEDIUM,
          type: GAME_TYPES.SO
        });
        initialForm[`so_gameMode`] = GAME_MODES.MEDIUM;
      }

      setCommonSettings(uniqueCommon);
      setLtSettings(lt);
      setLt1Settings(lt1);
      setRoSettings(ro);
      setSoSettings(so);
      setForm(initialForm);

    } catch (error) {
      dispatch(showToast({ type: "error", msg: error.message || "Failed to fetch settings" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    document.body.classList.add("settings-layout-fix");
    return () => {
      document.body.classList.remove("settings-layout-fix");
    };
  }, []);

  const normalizeId = (value) => {
    if (!value) return "";
    if (typeof value === "object") return String(value._id || value.id || value.operatorId || "").toLowerCase();
    return String(value).toLowerCase();
  };

  const getOperatorId = (operator) => {
    const raw = operator?._id || operator?.id || operator?.operatorId || operator?.value || operator?.operator?._id;
    return normalizeId(raw);
  };

  const getOperatorName = (operator) => {
    return operator?.username || operator?.name || operator?.operatorName || operator?.label || operator?.operator?.username || "Unknown Operator";
  };

  const getOperatorGameplayIdentity = (item) => {
    const rawType = String(item?.type || "").toLowerCase();
    const userMatch = rawType.match(/^user_(.+)_(lt1|lt|ro)$/);
    if (userMatch) {
      return {
        userId: normalizeId(userMatch[1]),
        gameType: userMatch[2]
      };
    }
    const match = rawType.match(/^operator_(.+)_(lt1|lt|ro)$/);
    if (match) {
      return {
        operatorId: normalizeId(match[1]),
        gameType: match[2]
      };
    }
    return {
      operatorId: normalizeId(item?.operatorId),
      userId: normalizeId(item?.userId),
      gameType: String(item?.gameType || "").toLowerCase()
    };
  };

  const getGlobalModeValue = (gameType) => {
    if (gameType === GAME_TYPES.LT) return form["lt_gameplay_params"] || GAME_MODES.MEDIUM;
    if (gameType === GAME_TYPES.LT1) return form["lt1_gameplay_params"] || GAME_MODES.MEDIUM;
    if (gameType === GAME_TYPES.RO) return form["ro_gameMode"] || GAME_MODES.HARD;
    if (gameType === GAME_TYPES.SO) return form["so_gameMode"] || GAME_MODES.HARD;
    return GAME_MODES.MEDIUM;
  };

  const getOperatorModeValue = (operatorId, gameType) => {
    if (!operatorId) return "";
    const normOpId = normalizeId(operatorId);
    const normGameType = String(gameType || "").toLowerCase();
    const found = operatorGameplayParams.find(
      (item) => {
        const identity = getOperatorGameplayIdentity(item);
        return (
          !identity.userId &&
          identity.operatorId === normOpId &&
          identity.gameType === normGameType
        );
      }
    );
    return found?.value ? String(found.value).toLowerCase() : "";
  };

  const getUserModeValue = (userId, gameType) => {
    if (!userId) return "";
    const normUserId = normalizeId(userId);
    const normGameType = String(gameType || "").toLowerCase();
    const found = operatorGameplayParams.find(
      (item) => {
        const identity = getOperatorGameplayIdentity(item);
        return (
          identity.userId === normUserId &&
          identity.gameType === normGameType
        );
      }
    );
    return found?.value ? String(found.value).toLowerCase() : "";
  };

  const getOperatorOptions = () => {
    return operators.map((operator) => {
      const id = getOperatorId(operator);
      if (!id) return null;
      const name = getOperatorName(operator);
      return {
        value: id,
        label: name
      };
    }).filter(Boolean);
  };

  const loadOperatorGameplayData = React.useCallback(async () => {
    setLoadingOperatorGameplay(true);
    try {
      const [operatorsAllRes, operatorsDropdownRes, gameplayResponse] = await Promise.allSettled([
        getOperators({ page: 1, limit: 1000 }),
        getOperatorDropdown(),
        fetchOperatorGameplayParams()
      ]);

      const allOperatorsPayload = operatorsAllRes.status === "fulfilled" ? operatorsAllRes.value : null;
      const dropdownOperatorsPayload = operatorsDropdownRes.status === "fulfilled" ? operatorsDropdownRes.value : null;

      const allOperators = Array.isArray(allOperatorsPayload?.data?.operators)
        ? allOperatorsPayload.data.operators
        : Array.isArray(allOperatorsPayload?.data)
          ? allOperatorsPayload.data
          : [];

      const dropdownOperators = Array.isArray(dropdownOperatorsPayload?.data?.operators)
        ? dropdownOperatorsPayload.data.operators
        : Array.isArray(dropdownOperatorsPayload?.data)
          ? dropdownOperatorsPayload.data
          : [];

      setOperators(allOperators.length ? allOperators : dropdownOperators);

      const gameplayPayload = gameplayResponse.status === "fulfilled" ? gameplayResponse.value : null;
      if (
        gameplayPayload &&
        (gameplayPayload.statusCode === 200 || gameplayPayload.status > 0 || gameplayPayload.status === "success" || gameplayPayload.success) &&
        Array.isArray(gameplayPayload.data)
      ) {
        setOperatorGameplayParams(gameplayPayload.data);
        return gameplayPayload.data;
      } else {
        setOperatorGameplayParams([]);
        return [];
      }

      // Intentionally do not toast on background load failures.
      // This page can still function and we don't want noisy error messages.
    } catch (error) {
      // Silent fail for background load
      console.error("Failed to load operator gameplay settings", error);
    } finally {
      setLoadingOperatorGameplay(false);
    }
  }, []);

  useEffect(() => {
    loadOperatorGameplayData();
  }, [loadOperatorGameplayData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOperatorSelectChange = async (selectedOption) => {
    const operatorId = selectedOption ? selectedOption.value : "";
    setSelectedUserId("");
    setOperatorUsers([]);

    if (operatorId) {
      setLoadingOperatorStats(true);
      setLoadingOperatorUsers(true);

      try {
        const [usersRes, gamesRes] = await Promise.allSettled([
          getOperatorUsers(operatorId),
          getOperatorGames(operatorId)
        ]);

        if (usersRes.status === "fulfilled" && usersRes.value) {
          const uData = usersRes.value?.data?.users || usersRes.value?.data || usersRes.value?.users || [];
          setOperatorUsers(Array.isArray(uData) ? uData : []);
        }

        const response = gamesRes.status === "fulfilled" ? gamesRes.value : null;
        const gameData = response?.data?.games || response?.games || [];

        let ltLevel = "";
        let lt1Level = "";
        let roLevel = "";

        if (Array.isArray(gameData)) {
          gameData.forEach(game => {
            const name = game.name || "";
            const level = game.gameplayType || "";

            if (name === "Lucky Target") ltLevel = level;
            else if (name === "Lucky Target 1") lt1Level = level;
            else if (name === "American Roulette") roLevel = level;
          });
        }

        setOperatorGameplayForm({
          operatorId,
          userId: "",
          lt: getOperatorModeValue(operatorId, GAME_TYPES.LT) || ltLevel || "",
          lt1: getOperatorModeValue(operatorId, GAME_TYPES.LT1) || lt1Level || "",
          ro: getOperatorModeValue(operatorId, GAME_TYPES.RO) || roLevel || ""
        });

        setOperatorStats(Array.isArray(gameData) ? gameData.reduce((acc, game) => {
          const key = game.name === "Lucky Target" ? "lt" :
            game.name === "Lucky Target 1" ? "lt1" :
              game.name === "American Roulette" ? "ro" : null;
          if (key) acc[key] = game;
          return acc;
        }, {}) : null);

      } catch (error) {
        console.error("Error fetching operator details:", error);
        setOperatorStats(null);
        setOperatorGameplayForm({
          operatorId,
          userId: "",
          lt: getOperatorModeValue(operatorId, GAME_TYPES.LT) || "",
          lt1: getOperatorModeValue(operatorId, GAME_TYPES.LT1) || "",
          ro: getOperatorModeValue(operatorId, GAME_TYPES.RO) || ""
        });
      } finally {
        setLoadingOperatorStats(false);
        setLoadingOperatorUsers(false);
      }
    } else {
      setOperatorGameplayForm({ operatorId: "", userId: "", lt: "", lt1: "", ro: "" });
      setOperatorStats(null);
      setOperatorUsers([]);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    if (!userId) {
      setOperatorGameplayForm(prev => ({
        ...prev,
        userId: "",
        lt: getOperatorModeValue(prev.operatorId, GAME_TYPES.LT) || "",
        lt1: getOperatorModeValue(prev.operatorId, GAME_TYPES.LT1) || "",
        ro: getOperatorModeValue(prev.operatorId, GAME_TYPES.RO) || ""
      }));
    } else {
      setOperatorGameplayForm(prev => ({
        ...prev,
        userId,
        lt: getUserModeValue(userId, GAME_TYPES.LT) || "",
        lt1: getUserModeValue(userId, GAME_TYPES.LT1) || "",
        ro: getUserModeValue(userId, GAME_TYPES.RO) || ""
      }));
    }
  };

  const handleOperatorGameplayChange = (gameType, value) => {
    setOperatorGameplayForm((prev) => ({ ...prev, [gameType]: value }));
  };

  const ReactSelectClearIndicator = (props) => {
    const {
      innerProps,
      selectProps
    } = props;

    if (selectProps?.isDisabled) return null;

    return (
      <div {...innerProps} className="react-select__clear-indicator" aria-hidden="true">
        <XIcon size={16} />
      </div>
    );
  };

  const operatorGameplaySelectComponents = {
    ClearIndicator: ReactSelectClearIndicator,
  };

  const handleOperatorGameplaySubmit = async (e) => {
    e.preventDefault();
    if (!operatorGameplayForm.operatorId) {
      dispatch(showToast({ type: "warning", msg: "Please select an operator" }));
      return;
    }

    setSavingOperatorGameplay(true);
    try {
      const payloadBase = selectedUserId
        ? { userId: selectedUserId, operatorId: operatorGameplayForm.operatorId }
        : { operatorId: operatorGameplayForm.operatorId };

      const results = await Promise.all([
        updateOperatorGameplayParams({
          ...payloadBase,
          gameType: GAME_TYPES.LT,
          value: operatorGameplayForm.lt ?? ""
        }),
        updateOperatorGameplayParams({
          ...payloadBase,
          gameType: GAME_TYPES.LT1,
          value: operatorGameplayForm.lt1 ?? ""
        }),
        updateOperatorGameplayParams({
          ...payloadBase,
          gameType: GAME_TYPES.RO,
          value: operatorGameplayForm.ro ?? ""
        })
      ]);
      const hasSuccess = results.some(r => r && (r.status > 0 || r.status === "success" || r.statusCode === 200 || r.success));

      if (hasSuccess) {
        const targetText = selectedUserId ? "User gameplay settings" : "Operator gameplay settings";
        dispatch(showToast({ type: "success", msg: `${targetText} updated successfully` }));
        const freshParams = await loadOperatorGameplayData();

        if (selectedUserId) {
          const normUserId = normalizeId(selectedUserId);
          const findMode = (gType) => {
            const item = (freshParams || []).find(p => {
              const identity = getOperatorGameplayIdentity(p);
              return identity.userId === normUserId && identity.gameType === String(gType).toLowerCase();
            });
            return item?.value ? String(item.value).toLowerCase() : "";
          };
          setOperatorGameplayForm(prev => ({
            ...prev,
            lt: findMode(GAME_TYPES.LT),
            lt1: findMode(GAME_TYPES.LT1),
            ro: findMode(GAME_TYPES.RO)
          }));
        } else if (operatorGameplayForm.operatorId) {
          const normOpId = normalizeId(operatorGameplayForm.operatorId);
          const findMode = (gType) => {
            const item = (freshParams || []).find(p => {
              const identity = getOperatorGameplayIdentity(p);
              return !identity.userId && identity.operatorId === normOpId && identity.gameType === String(gType).toLowerCase();
            });
            return item?.value ? String(item.value).toLowerCase() : "";
          };
          setOperatorGameplayForm(prev => ({
            ...prev,
            lt: findMode(GAME_TYPES.LT),
            lt1: findMode(GAME_TYPES.LT1),
            ro: findMode(GAME_TYPES.RO)
          }));
        }
      } else {
        dispatch(showToast({ type: "error", msg: "Failed to update gameplay settings" }));
      }
    } catch (error) {
      dispatch(showToast({ type: "error", msg: error.message || "Failed to update gameplay settings" }));
    } finally {
      setSavingOperatorGameplay(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updatePromises = [];

      // Logic to process value based on original type
      const processValue = (newValue, oldValue) => {
        if (typeof oldValue === 'number' || (!isNaN(oldValue) && oldValue !== '' && typeof oldValue !== 'string')) {
          return newValue === '' ? 0 : Number(newValue);
        }
        return newValue;
      };

      // 1. Process Common Settings
      commonSettings.forEach((setting) => {
        const newValue = form[setting.name];
        if (newValue !== undefined && newValue !== setting.value) {
          updatePromises.push(updateAppSettings({
            name: setting.name,
            value: processValue(newValue, setting.value),
            type: setting.type || GAME_TYPES.ALL
          }));
        }
      });

      // 2. Process LT Settings
      ltSettings.forEach((setting) => {
        const newValue = form[`lt_${setting.name}`];
        if (newValue !== undefined && newValue !== setting.value) {
          if (setting.name === "gameplay_params") {
            // Specialized endpoint for gameplay difficulty
            updatePromises.push(updateGameplayParams({
              type: GAME_TYPES.LT,
              value: newValue
            }));
          } else {
            updatePromises.push(updateAppSettings({
              value: processValue(newValue, setting.value),
              type: GAME_TYPES.LT
            }));
          }
        }
      });

      // 3. Process LT1 Settings
      lt1Settings.forEach((setting) => {
        const newValue = form[`lt1_${setting.name}`];
        if (newValue !== undefined && newValue !== setting.value) {
          if (setting.name === "gameplay_params") {
            updatePromises.push(updateGameplayParams({
              type: GAME_TYPES.LT1,
              value: newValue
            }));
          } else {
            updatePromises.push(updateAppSettings({
              name: setting.name,
              value: processValue(newValue, setting.value),
              type: GAME_TYPES.LT1
            }));
          }
        }
      });

      // 4. Process RO Settings
      roSettings.forEach((setting) => {
        const newValue = form[`ro_${setting.name}`];
        if (newValue !== undefined && newValue !== setting.value) {
          updatePromises.push(updateSettingsRO({
            [setting.name]: newValue
          }));
        }
      });

      // 5. Process SO Settings
      soSettings.forEach((setting) => {
        const newValue = form[`so_${setting.name}`];
        if (newValue !== undefined && newValue !== setting.value) {
          updatePromises.push(updateSettingsSO({
            [setting.name]: newValue
          }));
        }
      });


      if (updatePromises.length === 0) {
        dispatch(showToast({ type: "info", msg: "No changes to update" }));
        setLoading(false);
        return;
      }

      const results = await Promise.all(updatePromises);
      const hasSuccess = results.some(r => r && (r.status === "success" || r.status === 1 || r.success === true));


      if (hasSuccess) {
        dispatch(showToast({ type: "success", msg: "Settings updated successfully" }));
        loadSettings();
      } else {
        dispatch(showToast({ type: "error", msg: "Failed to update settings" }));
      }
    } catch (error) {
      dispatch(showToast({ type: "error", msg: error.message || "Failed to update settings" }));
    } finally {
      setLoading(false);
    }
  };

  const getInputType = (value) => {
    if (typeof value === 'number' || (!isNaN(value) && value !== '' && typeof value !== 'string')) {
      return 'number';
    }
    return 'text';
  };

  const formatLabel = (name) => {
    if (name === "gameplay_params" || name === "gameMode") return "Game Play";
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const renderSettingField = (setting, prefix = "") => {
    const fieldName = prefix ? `${prefix}_${setting.name}` : setting.name;
    const inputType = getInputType(setting.value);
    const label = formatLabel(setting.name);

    return (
      <FormGroup key={setting._id || fieldName} className="setting-field mb-0">
        <Label for={fieldName} className="form-label setting-label">{label}</Label>
        {(setting.name === "gameplay_params" || setting.name === "gameMode") ? (
          <Select
            id={fieldName}
            name={fieldName}
            value={[
              { value: GAME_MODES.MORE_EASY, label: "More Easy" },
              { value: GAME_MODES.EASY, label: "Easy" },
              { value: GAME_MODES.MEDIUM, label: "Medium" },
              { value: GAME_MODES.HARD, label: "Hard" }
            ].find(opt => opt.value === (form[fieldName] ?? ""))}
            options={[
              { value: GAME_MODES.MORE_EASY, label: "More Easy" },
              { value: GAME_MODES.EASY, label: "Easy" },
              { value: GAME_MODES.MEDIUM, label: "Medium" },
              { value: GAME_MODES.HARD, label: "Hard" }
            ]}
            onChange={(selected) => handleChange({ target: { name: fieldName, value: selected ? selected.value : "" } })}
            isDisabled={loading}
            classNamePrefix="react-select"
            placeholder="Select gameplay level"
          />
        ) : (
          <Input
            id={fieldName}
            name={fieldName}
            type={inputType}
            className="setting-input form-control"
            value={form[fieldName] ?? ""}
            onChange={handleChange}
            placeholder={`Enter ${label.toLowerCase()}`}
            disabled={loading}
            min={inputType === 'number' ? 0 : undefined}
            step={inputType === 'number' ? 'any' : undefined}
          />
        )}
      </FormGroup>
    );
  };

  const renderSettingsRow = (settingsList, prefix = "") => {
    if (!settingsList.length) return null;

    const rows = [];
    for (let i = 0; i < settingsList.length; i += 2) {
      const leftSetting = settingsList[i];
      const rightSetting = settingsList[i + 1];

      rows.push(
        <Row key={i} className="g-3 mb-3">
          <Col md={rightSetting ? 6 : 12}>
            <div className="setting-input-card">
              {renderSettingField(leftSetting, prefix)}
            </div>
          </Col>
          {rightSetting && (
            <Col md={6}>
              <div className="setting-input-card">
                {renderSettingField(rightSetting, prefix)}
              </div>
            </Col>
          )}
        </Row>
      );
    }
    return rows;
  };

  const hasAnySettings = commonSettings.length > 0 || ltSettings.length > 0 || lt1Settings.length > 0 || roSettings.length > 0;
  const isOnlyDefaultCoin =
    commonSettings.length === 1 &&
    commonSettings[0]?.name === "welcomeBalance";
  const settingSections = [
    {
      key: "common",
      title: "Default Coin",
      description: "Default coin value for new users",
      className: "section-common",
      settings: commonSettings,
      prefix: ""
    },
    {
      key: "lt",
      title: "Lucky Target Game",
      description: "Configuration specific to Lucky Target",
      className: "section-lt",
      settings: ltSettings,
      prefix: "lt"
    },
    {
      key: "lt1",
      title: "Lucky Target 1 Game",
      description: "Configuration specific to Lucky Target 1",
      className: "section-lt1",
      settings: lt1Settings,
      prefix: "lt1"
    },
    {
      key: "ro",
      title: "American Roulette Game",
      description: "Configuration specific to American Roulette",
      className: "section-ro",
      settings: roSettings,
      prefix: "ro"
    },
    {
      key: "so",
      title: "Sorat Game",
      description: "Configuration specific to Sorat",
      className: "section-so",
      settings: soSettings,
      prefix: "so"
    }
  ];
  const visibleSections = settingSections.filter((section) => section.settings.length > 0);

  return (
    <div className="page-content settings-page-content">
      <Container fluid>
        <BreadCrumb title="Settings" pageTitle="Global Settings" />
        <Row className="mt-4 mt-lg-5">
          <Col lg={12} className="mx-auto px-0">
            <div className="settings-common-wrapper">
              <div className="settings-section-header mb-3">
                <h5 className="mb-1 text-uppercase fw-bold">Common Settings</h5>
              </div>
              <Form onSubmit={handleSubmit}>
                <Card className="settings-card border-0">
                  <CardBody className="settings-body">
                    {hasAnySettings ? (
                      <>
                        {visibleSections.map((section, idx) => (
                          idx % 2 === 0 && (
                            <Row key={`settings-row-${idx}`} className="g-3 settings-grid-row">
                              <Col lg={6}>
                                <section className={`settings-section ${section.className}`}>
                                  <div className="settings-section-head pb-0">
                                    <h6 className="settings-section-title mb-0">{section.title}</h6>
                                  </div>
                                  <div className="settings-section-body p-2">
                                    {renderSettingsRow(section.settings, section.prefix)}
                                  </div>
                                </section>
                              </Col>
                              {visibleSections[idx + 1] && (
                                <Col lg={6}>
                                  <section className={`settings-section ${visibleSections[idx + 1].className}`}>
                                    <div className="settings-section-head pb-0">
                                      <h6 className="settings-section-title mb-0">{visibleSections[idx + 1].title}</h6>
                                    </div>
                                    <div className="settings-section-body p-2">
                                      {renderSettingsRow(visibleSections[idx + 1].settings, visibleSections[idx + 1].prefix)}
                                    </div>
                                  </section>
                                </Col>
                              )}
                            </Row>
                          )
                        ))}
                      </>
                    ) : (
                      <div className="text-center py-5 settings-empty-state">
                        <p className="text-muted mb-0">No settings available.</p>
                      </div>
                    )}

                    <div className="d-flex justify-content-center border-top settings-action-wrap">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={loading}
                        className="settings-submit-btn"
                        id="settings-submit-btn"
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              <span>Updating...</span>
                            </>
                          ) : (
                            <>
                              <SettingsIcon size={16} />
                              <span>Update Settings</span>
                            </>
                          )}
                        </div>
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Form>
            </div>

            <div className="operator-gameplay-wrapper">
              <div className="settings-section-header mb-3 d-flex align-items-center justify-between">
                <div>
                  <h5 className="mb-1 text-uppercase fw-bold">Operator Gameplay Settings</h5>
                  <p className="text-muted small mb-0">Select an operator to manage settings, or select a specific user to customize their individual game mode.</p>
                </div>
              </div>
              <Card className="border-0 mb-5">
                <CardBody className="settings-body">
                  <Form onSubmit={handleOperatorGameplaySubmit}>
                    {/* Selector Grid Row: Select Operator & Select User side-by-side */}
                    <Row className="mb-4 g-3">
                      <Col lg={6}>
                        <section className="settings-section section-common h-100">
                          <div className="settings-section-head pb-0">
                            <h6 className="settings-section-title mb-0">Select Operator</h6>
                          </div>
                          <div className="settings-section-body p-3">
                            <div className="setting-input-card">
                              <FormGroup className="setting-field mb-0">
                                <Label for="operatorId" className="form-label setting-label">Operator</Label>
                                <Select
                                  id="operatorId"
                                  name="operatorId"
                                  options={getOperatorOptions()}
                                  value={getOperatorOptions().find(opt => opt.value === operatorGameplayForm.operatorId) || null}
                                  onChange={handleOperatorSelectChange}
                                  isLoading={loadingOperatorGameplay}
                                  isDisabled={loadingOperatorGameplay || savingOperatorGameplay}
                                  placeholder={loadingOperatorGameplay ? "Loading operators..." : "Search and select operator..."}
                                  isClearable
                                  classNamePrefix="react-select"
                                  components={operatorGameplaySelectComponents}
                                />
                              </FormGroup>
                            </div>
                          </div>
                        </section>
                      </Col>

                      <Col lg={6}>
                        <section className="settings-section section-common h-100">
                          <div className="settings-section-head pb-0">
                            <h6 className="settings-section-title mb-0">Select User</h6>
                          </div>
                          <div className="settings-section-body p-3">
                            <div className="setting-input-card">
                              <FormGroup className="setting-field mb-0">
                                <Label for="userId" className="form-label setting-label">User</Label>
                                <Select
                                  id="userId"
                                  name="userId"
                                  options={[
                                    { value: "", label: "All Users (Operator Default)" },
                                    ...operatorUsers.map(user => {
                                      const uId = normalizeId(user._id || user.id);
                                      const accNo = user.acnumber || uId;
                                      return {
                                        value: uId,
                                        label: `${accNo}`
                                      };
                                    })
                                  ]}
                                  value={
                                    selectedUserId
                                      ? (() => {
                                          const foundUser = operatorUsers.find(u => normalizeId(u._id || u.id) === selectedUserId);
                                          if (!foundUser) return { value: "", label: "All Users (Operator Default)" };
                                          const uId = normalizeId(foundUser._id || foundUser.id);
                                          const accNo = foundUser.acnumber || uId;
                                          return {
                                            value: uId,
                                            label: `${accNo}`
                                          };
                                        })()
                                      : { value: "", label: "All Users (Operator Default)" }
                                  }
                                  onChange={(selectedOption) => {
                                    const newUserId = selectedOption ? selectedOption.value : "";
                                    handleUserSelect(newUserId);
                                  }}
                                  isLoading={loadingOperatorUsers}
                                  isDisabled={!operatorGameplayForm.operatorId || loadingOperatorUsers || savingOperatorGameplay}
                                  placeholder={
                                    !operatorGameplayForm.operatorId
                                      ? "First select an operator..."
                                      : loadingOperatorUsers
                                      ? "Loading users..."
                                      : "Select user..."
                                  }
                                  isClearable
                                  classNamePrefix="react-select"
                                  components={operatorGameplaySelectComponents}
                                />
                              </FormGroup>
                            </div>
                          </div>
                        </section>
                      </Col>
                    </Row>



                    {/* 3 Grid Game Mode Cards */}
                    <Row>
                      {/* Grid 1: Lucky Target */}
                      <Col lg={3} md={6} className="mb-3">
                        <section className="settings-section section-lt h-100">
                          <div className="settings-section-head pb-0">
                            <h6 className="settings-section-title mb-0">Lucky Target Game</h6>
                          </div>
                          <div className="settings-section-body p-3">
                            <div className="setting-input-card">
                              <FormGroup className="setting-field mb-0">
                                <Label className="form-label setting-label">Game Play Mode</Label>
                                <Select
                                  value={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ].find(opt => opt.value === (operatorGameplayForm.lt ?? "")) || { value: "", label: "Select mode" }}
                                  options={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ]}
                                  onChange={(selected) => handleOperatorGameplayChange("lt", selected ? selected.value : "")}
                                  isDisabled={!operatorGameplayForm.operatorId || savingOperatorGameplay}
                                  isClearable
                                  placeholder="Select mode"
                                  classNamePrefix="react-select"
                                  components={operatorGameplaySelectComponents}
                                />
                              </FormGroup>
                            </div>
                          </div>
                        </section>
                      </Col>

                      {/* Grid 2: Lucky Target 1 */}
                      <Col lg={3} md={6} className="mb-3">
                        <section className="settings-section section-lt1 h-100">
                          <div className="settings-section-head pb-0">
                            <h6 className="settings-section-title mb-0">Lucky Target 1 Game</h6>
                          </div>
                          <div className="settings-section-body p-3">
                            <div className="setting-input-card">
                              <FormGroup className="setting-field mb-0">
                                <Label className="form-label setting-label">Game Play Mode</Label>
                                <Select
                                  value={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ].find(opt => opt.value === (operatorGameplayForm.lt1 ?? "")) || { value: "", label: "Select mode" }}
                                  options={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ]}
                                  onChange={(selected) => handleOperatorGameplayChange("lt1", selected ? selected.value : "")}
                                  isDisabled={!operatorGameplayForm.operatorId || savingOperatorGameplay}
                                  isClearable
                                  placeholder="Select mode"
                                  classNamePrefix="react-select"
                                  components={operatorGameplaySelectComponents}
                                />
                              </FormGroup>
                            </div>
                          </div>
                        </section>
                      </Col>

                      {/* Grid 3: American Roulette */}
                      <Col lg={3} md={6} className="mb-3">
                        <section className="settings-section section-ro h-100">
                          <div className="settings-section-head pb-0">
                            <h6 className="settings-section-title mb-0">American Roulette Game</h6>
                          </div>
                          <div className="settings-section-body p-3">
                            <div className="setting-input-card">
                              <FormGroup className="setting-field mb-0">
                                <Label className="form-label setting-label">Game Play Mode</Label>
                                <Select
                                  value={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ].find(opt => opt.value === (operatorGameplayForm.ro ?? "")) || { value: "", label: "Select mode" }}
                                  options={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ]}
                                  onChange={(selected) => handleOperatorGameplayChange("ro", selected ? selected.value : "")}
                                  isDisabled={!operatorGameplayForm.operatorId || savingOperatorGameplay}
                                  isClearable
                                  placeholder="Select mode"
                                  classNamePrefix="react-select"
                                  components={operatorGameplaySelectComponents}
                                />
                              </FormGroup>
                            </div>
                          </div>
                        </section>
                      </Col>

                      {/* Grid 4: Sorat */}
                      <Col lg={3} md={6} className="mb-3">
                        <section className="settings-section section-so h-100">
                          <div className="settings-section-head pb-0">
                            <h6 className="settings-section-title mb-0">Sorat Game</h6>
                          </div>
                          <div className="settings-section-body p-3">
                            <div className="setting-input-card">
                              <FormGroup className="setting-field mb-0">
                                <Label className="form-label setting-label">Game Play Mode</Label>
                                <Select
                                  value={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ].find(opt => opt.value === (operatorGameplayForm.so ?? "")) || { value: "", label: "Select mode" }}
                                  options={[
                                    { value: "", label: "Select mode" },
                                    { value: GAME_MODES.MORE_EASY, label: "More Easy" },
                                    { value: GAME_MODES.EASY, label: "Easy" },
                                    { value: GAME_MODES.MEDIUM, label: "Medium" },
                                    { value: GAME_MODES.HARD, label: "Hard" }
                                  ]}
                                  onChange={(selected) => handleOperatorGameplayChange("so", selected ? selected.value : "")}
                                  isDisabled={!operatorGameplayForm.operatorId || savingOperatorGameplay}
                                  isClearable
                                  placeholder="Select mode"
                                  classNamePrefix="react-select"
                                  components={operatorGameplaySelectComponents}
                                />
                              </FormGroup>
                            </div>
                          </div>
                        </section>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-center border-top settings-action-wrap operator-action-wrap mt-2 pt-3">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={!operatorGameplayForm.operatorId || savingOperatorGameplay}
                        className="settings-submit-btn"
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          {savingOperatorGameplay ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              <span>Updating...</span>
                            </>
                          ) : (
                            <>
                              <SettingsIcon size={16} />
                              <span>{selectedUserId ? "Update Selected User Game Mode" : "Update Operator Game Mode"}</span>
                            </>
                          )}
                        </div>
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Settings;
